import { useEffect, useMemo, useState } from "preact/hooks";
import { createPortal } from "preact/compat";
import { useLocation } from "wouter";
import {
  Invoices,
  Settings,
  Customers,
  type ItemPayload,
  type InvoiceCreatePayload,
} from "../lib/api";
import type { Customer, Supplier } from "@shared/types";
import { CustomerInlinePicker } from "../components/CustomerInlinePicker";
import { vsFromNumber } from "../lib/numbering-client";
import { addDaysIso, todayIso } from "../lib/date-client";
import { describeError } from "../lib/errors";
import { invoiceTotals, lineTotals } from "@shared/totals";
import { formatMoney, formatMoneyPlain, formatDateSk } from "@shared/format";

interface Props {
  slug?: string;
}

interface FormItem {
  name: string;
  quantity: string;
  unit: string;
  unit_price_net: string;
  vat_rate: string;
  sort_order: number;
}

const defaultItem = (sort_order = 0): FormItem => ({
  name: "[UR] Konzultačné služby",
  quantity: "10",
  unit: "hod.",
  unit_price_net: "75",
  vat_rate: "23",
  sort_order,
});

export function InvoiceEditor({ slug }: Props) {
  const [, setLocation] = useLocation();
  const isNew = slug === undefined;

  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customer, setCustomer] = useState<Customer | null>(null);

  const today = todayIso();

  const [status, setStatus] = useState<"draft" | "issued">("draft");
  const [number, setNumber] = useState("");
  const [numberManuallyEdited, setNumberManuallyEdited] = useState(false);
  const [issueDate, setIssueDate] = useState(today);
  const [deliveryDate, setDeliveryDate] = useState(today);
  const [dueDate, setDueDate] = useState(addDaysIso(today, 14));
  const [variableSymbol, setVariableSymbol] = useState("");
  const [vsManuallyEdited, setVsManuallyEdited] = useState(false);
  const [constantSymbol, setConstantSymbol] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Prevodom");
  const [note, setNote] = useState("");
  const [showNote, setShowNote] = useState(false);
  const [includeSignature, setIncludeSignature] = useState(false);
  const [items, setItems] = useState<FormItem[]>([defaultItem(0)]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<"draft" | "issued" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [navSlot, setNavSlot] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setNavSlot(document.getElementById("nav-page-actions"));
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const [sup, cs] = await Promise.all([
          Settings.getSupplier().catch(() => null),
          Customers.list(),
        ]);
        if (cancelled) return;
        setSupplier(sup?.supplier ?? null);
        setCustomers(cs.customers);
        setIncludeSignature(Boolean(sup?.supplier?.signature_image_b64));

        if (isNew) {
          // New invoice → display the next issued slot. Server still stores
          // NULL number/VS until the user saves with status "issued".
          const next = await Invoices.nextNumber(today);
          if (cancelled) return;
          setNumber(next.number);
          setVariableSymbol(next.variable_symbol);
          if (cs.customers.length === 1 && cs.customers[0]) {
            setCustomer(cs.customers[0]);
          }
        } else {
          const detail = await Invoices.get(slug!);
          if (cancelled) return;
          if (detail.invoice.einvoice_exported_at) {
            // Exported e-invoices are immutable; the detail page says why.
            setLocation(`/invoices/${slug}`, { replace: true });
            return;
          }
          setCustomer(detail.customer);
          setStatus(detail.invoice.status);
          setIssueDate(detail.invoice.issue_date);
          setDeliveryDate(detail.invoice.delivery_date);
          setDueDate(detail.invoice.due_date);
          setConstantSymbol(detail.invoice.constant_symbol ?? "");
          setPaymentMethod(detail.invoice.payment_method);
          setNote(detail.invoice.note ?? "");
          setShowNote(Boolean(detail.invoice.note));
          setItems(
            detail.items.map((it, idx) => ({
              name: it.name,
              quantity: String(it.quantity),
              unit: it.unit,
              unit_price_net: String(it.unit_price_net),
              vat_rate: String(it.vat_rate),
              sort_order: it.sort_order ?? idx,
            }))
          );
          if (detail.invoice.status === "issued") {
            setNumber(detail.invoice.number ?? "");
            setVariableSymbol(detail.invoice.variable_symbol ?? "");
          } else {
            // Draft: show the would-be-issued slot, computed from issue_date.
            const next = await Invoices.nextNumber(detail.invoice.issue_date);
            if (cancelled) return;
            setNumber(next.number);
            setVariableSymbol(next.variable_symbol);
          }
        }
      } catch (e) {
        if (!cancelled) setError(describeError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [slug, isNew]);

  function onNumberChange(v: string) {
    setNumber(v);
    setNumberManuallyEdited(true);
    setVariableSymbol((prev) =>
      prev === vsFromNumber(number) || prev === "" ? vsFromNumber(v) : prev
    );
  }

  function onIssueChange(v: string) {
    const prevYear = issueDate.slice(0, 4);
    const newYear = v.slice(0, 4);
    setIssueDate(v);
    setDeliveryDate((prev) => (prev === issueDate ? v : prev));
    setDueDate((prev) =>
      prev === addDaysIso(issueDate, 14) ? addDaysIso(v, 14) : prev
    );
    // Drafts always preview the would-be-issued slot for the chosen year.
    const isDraft = status === "draft";
    if (isDraft && !numberManuallyEdited && prevYear !== newYear) {
      Invoices.nextNumber(v)
        .then((next) => {
          setNumber(next.number);
          setVariableSymbol(next.variable_symbol);
        })
        .catch(() => {});
    }
  }

  function updateItem(idx: number, patch: Partial<FormItem>) {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }

  function addItem() {
    setItems((prev) => [...prev, defaultItem(prev.length)]);
  }

  function removeItem(idx: number) {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  }

  const numericItems = useMemo(
    () =>
      items.map((it) => ({
        quantity: Number(it.quantity) || 0,
        unit_price_net: Number(it.unit_price_net) || 0,
        vat_rate: Number(it.vat_rate) || 0,
      })),
    [items]
  );
  const totals = useMemo(() => invoiceTotals(numericItems), [numericItems]);

  async function save(targetStatus: "draft" | "issued") {
    if (!customer) {
      setError("Vyberte odberateľa");
      return;
    }
    setSaving(targetStatus);
    setError(null);
    const payloadItems: ItemPayload[] = items.map((it, idx) => ({
      name: it.name.trim(),
      quantity: Number(it.quantity) || 0,
      unit: it.unit,
      unit_price_net: Number(it.unit_price_net) || 0,
      vat_rate: Number(it.vat_rate) || 0,
      sort_order: idx,
    }));
    try {
      // For drafts the server stores NULL; for issued, only forward the manual
      // override (otherwise the server picks the next slot).
      const sendNumber =
        targetStatus === "issued" && numberManuallyEdited ? number : null;
      const sendVs =
        targetStatus === "issued" && vsManuallyEdited ? variableSymbol : null;
      const slugFor = (inv: { id: number; number: string | null }) =>
        inv.number ?? String(inv.id);
      if (isNew) {
        const body: InvoiceCreatePayload = {
          customer_id: customer.id,
          number: sendNumber,
          issue_date: issueDate,
          delivery_date: deliveryDate,
          due_date: dueDate,
          variable_symbol: sendVs,
          constant_symbol: constantSymbol || null,
          payment_method: paymentMethod,
          note: note || null,
          status: targetStatus,
          items: payloadItems,
        };
        const r = await Invoices.create(body);
        setLocation(`/invoices/${slugFor(r.invoice)}`);
      } else {
        const r = await Invoices.update(slug!, {
          customer_id: customer.id,
          number: sendNumber,
          issue_date: issueDate,
          delivery_date: deliveryDate,
          due_date: dueDate,
          variable_symbol: sendVs,
          constant_symbol: constantSymbol || null,
          payment_method: paymentMethod,
          note: note || null,
          status: targetStatus,
          items: payloadItems,
        });
        setLocation(`/invoices/${slugFor(r.invoice)}`);
      }
    } catch (e) {
      setError(describeError(e));
      setSaving(null);
    }
  }

  if (loading) {
    return (
      <main class="paper-shell">
        <p class="muted">Načítavam…</p>
      </main>
    );
  }

  const saveButtons = (
    <>
      <button
        type="button"
        class="btn btn--sm"
        disabled={saving !== null}
        onClick={() => save("draft")}
      >
        {saving === "draft" ? "Ukladám…" : "Uložiť koncept"}
      </button>
      <button
        type="button"
        class="btn btn--sm primary"
        disabled={saving !== null}
        onClick={() => save("issued")}
      >
        {saving === "issued" ? "Ukladám…" : "Uložiť a vystaviť"}
      </button>
    </>
  );

  return (
    <main class="paper-shell">
      {navSlot && createPortal(saveButtons, navSlot)}
      {error && <p class="error">{error}</p>}
      <article class="paper">
        <header class="paper__head">
          <div class="paper__supplier">
            {supplier ? (
              <>
                <span class="paper__label">Dodávateľ</span>
                <h2>{supplier.name}</h2>
                <p>
                  {[
                    supplier.address_line1,
                    `${supplier.address_zip} ${supplier.address_city}`.trim(),
                    supplier.address_country,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </p>
                <p class="paper__ids">
                  {[
                    supplier.ico && `IČO: ${supplier.ico}`,
                    supplier.dic && `DIČ: ${supplier.dic}`,
                    supplier.ic_dph
                      ? `IČ DPH: ${supplier.ic_dph}`
                      : "Nie je platiteľom DPH",
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </>
            ) : (
              <p class="muted">
                Dodávateľ — nastavte v <a href="/settings">Nastaveniach</a>.
              </p>
            )}
          </div>
          <div class="paper__title">
            <span class="paper__label">Faktúra — daňový doklad</span>
            <input
              class="inline-input inline-input--xl"
              value={number}
              onInput={(e) => onNumberChange((e.target as HTMLInputElement).value)}
              size={Math.max(number.length + 1, 12)}
              aria-label="Číslo faktúry"
            />
          </div>
        </header>

        <section class="paper__parties">
          <div class="paper__customer">
            <span class="paper__label">Odberateľ</span>
            <CustomerInlinePicker
              customer={customer}
              customers={customers}
              onChange={(c) => {
                setCustomer(c);
                setCustomers((prev) =>
                  prev.find((p) => p.id === c.id) ? prev : [...prev, c]
                );
              }}
            />
          </div>
          <dl class="paper__meta">
            <dt>Dátum vystavenia</dt>
            <dd>
              <input
                type="date"
                class="inline-input"
                value={issueDate}
                onInput={(e) =>
                  onIssueChange((e.target as HTMLInputElement).value)
                }
              />
            </dd>
            <dt>Dátum dodania</dt>
            <dd>
              <input
                type="date"
                class="inline-input"
                value={deliveryDate}
                onInput={(e) =>
                  setDeliveryDate((e.target as HTMLInputElement).value)
                }
              />
            </dd>
            <dt>Dátum splatnosti</dt>
            <dd>
              <input
                type="date"
                class="inline-input"
                value={dueDate}
                onInput={(e) => setDueDate((e.target as HTMLInputElement).value)}
              />
            </dd>
            <dt>Spôsob úhrady</dt>
            <dd>
              <input
                class="inline-input"
                value={paymentMethod}
                onInput={(e) =>
                  setPaymentMethod((e.target as HTMLInputElement).value)
                }
                size={Math.max(paymentMethod.length, 8)}
              />
            </dd>
            <dt>Variabilný symbol</dt>
            <dd>
              <input
                class="inline-input inline-input--num"
                value={variableSymbol}
                onInput={(e) => {
                  setVariableSymbol((e.target as HTMLInputElement).value);
                  setVsManuallyEdited(true);
                }}
                size={Math.max(variableSymbol.length, 6)}
              />
            </dd>
            <dt>Konštantný symbol</dt>
            <dd>
              <input
                class="inline-input inline-input--num"
                value={constantSymbol}
                onInput={(e) =>
                  setConstantSymbol((e.target as HTMLInputElement).value)
                }
                placeholder="—"
                size={6}
              />
            </dd>
          </dl>
        </section>

        <table class="paper__items">
          <thead>
            <tr>
              <th class="paper__items-name">Názov a popis položky</th>
              <th class="num paper__items-qty">Počet</th>
              <th class="paper__items-unit">Jedn.</th>
              <th class="num paper__items-price" title="Jednotková cena bez DPH">
                Cena bez DPH
              </th>
              <th class="num paper__items-vat">DPH</th>
              <th class="num paper__items-total">Celkom bez DPH</th>
              <th class="num" title="Podiel na celkovej sume">Podiel</th>
              <th class="paper__items-action" aria-label="Akcie" />
            </tr>
          </thead>
          <tbody>
            {items.map((it, idx) => {
              const qty = Number(it.quantity) || 0;
              const price = Number(it.unit_price_net) || 0;
              const { net } = lineTotals({
                quantity: qty,
                unit_price_net: price,
                vat_rate: Number(it.vat_rate) || 0,
              });
              const share = totals.net > 0 ? (net / totals.net) * 100 : 0;
              return (
                <tr key={idx}>
                  <td>
                    <input
                      class="inline-input inline-input--block"
                      value={it.name}
                      onInput={(e) =>
                        updateItem(idx, {
                          name: (e.target as HTMLInputElement).value,
                        })
                      }
                      placeholder="Názov položky"
                    />
                  </td>
                  <td class="num">
                    <input
                      class="inline-input inline-input--num inline-input--qty"
                      type="number"
                      min="0"
                      step="any"
                      value={it.quantity}
                      onInput={(e) =>
                        updateItem(idx, {
                          quantity: (e.target as HTMLInputElement).value,
                        })
                      }
                    />
                  </td>
                  <td>
                    <input
                      class="inline-input"
                      value={it.unit}
                      onInput={(e) =>
                        updateItem(idx, {
                          unit: (e.target as HTMLInputElement).value,
                        })
                      }
                      size={4}
                    />
                  </td>
                  <td class="num">
                    <input
                      class="inline-input inline-input--num"
                      inputMode="decimal"
                      value={it.unit_price_net}
                      onInput={(e) =>
                        updateItem(idx, {
                          unit_price_net: (e.target as HTMLInputElement).value,
                        })
                      }
                      size={5}
                    />
                  </td>
                  <td class="num">
                    <input
                      class="inline-input inline-input--num"
                      inputMode="decimal"
                      value={it.vat_rate}
                      onInput={(e) =>
                        updateItem(idx, {
                          vat_rate: (e.target as HTMLInputElement).value,
                        })
                      }
                      size={2}
                    />
                    &nbsp;%
                  </td>
                  <td class="num">{formatMoneyPlain(net)}</td>
                  <td class="num paper__items-share">
                    {totals.net > 0 ? `${share.toFixed(1)} %` : "—"}
                  </td>
                  <td class="paper__items-action">
                    <button
                      type="button"
                      class="paper__row-del"
                      onClick={() => removeItem(idx)}
                      disabled={items.length === 1}
                      aria-label="Odstrániť položku"
                      title="Odstrániť"
                    >
                      ×
                    </button>
                  </td>
                </tr>
              );
            })}
            <tr class="paper__items-add-row">
              <td colSpan={8}>
                <button
                  type="button"
                  class="paper__items-add"
                  onClick={addItem}
                >
                  + Pridať položku
                </button>
              </td>
            </tr>
          </tbody>
        </table>

        <section class="paper__totals">
          <table>
            <thead>
              <tr>
                <th>Sadzba DPH</th>
                <th class="num">Základ DPH</th>
                <th class="num">Výška DPH</th>
              </tr>
            </thead>
            <tbody>
              {totals.vatBreakdown.map((row) => (
                <tr key={row.rate}>
                  <td>{row.rate} %</td>
                  <td class="num">{formatMoneyPlain(row.base)}</td>
                  <td class="num">{formatMoneyPlain(row.vat)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div class="paper__grand">
            <span>Celková suma</span>
            <strong>{formatMoney(totals.gross)}</strong>
          </div>
        </section>

        {supplier?.iban && (
          <section class="paper__payment">
            <div class="paper__payment-qr">
              <span class="paper__paybysquare">
                <span class="muted">PAY</span> <strong>by square</strong>
              </span>
              <div class="paper__payment-qr-placeholder" aria-hidden="true" />
            </div>
            <dl class="paper__payment-details">
              <dt class="paper__label">Platba</dt>
              <dd class="paper__payment-bank">
                {supplier.bank_name || "—"}
              </dd>
              <dt>IBAN</dt>
              <dd>{formatIban(supplier.iban)}</dd>
              {supplier.swift && (
                <>
                  <dt>SWIFT</dt>
                  <dd>{supplier.swift}</dd>
                </>
              )}
              <dt>Variabilný symbol</dt>
              <dd>{variableSymbol}</dd>
              <dt>Dátum splatnosti</dt>
              <dd>{formatDateSk(dueDate)}</dd>
              <dt>Suma na úhradu</dt>
              <dd class="paper__payment-amount">{formatMoney(totals.gross)}</dd>
            </dl>
          </section>
        )}

        {supplier?.signature_image_b64 && (
          <div class="paper__signature-row">
            <div class="paper__signature">
              {includeSignature && (
                <img
                  src={`data:image/png;base64,${supplier.signature_image_b64}`}
                  alt="Podpis"
                />
              )}
              <label class="paper__signature-toggle">
                <input
                  type="checkbox"
                  checked={includeSignature}
                  onChange={(e) =>
                    setIncludeSignature(
                      (e.target as HTMLInputElement).checked
                    )
                  }
                />
                <span>Podpis a pečiatka</span>
              </label>
            </div>
          </div>
        )}

        {showNote ? (
          <section class="paper__note">
            <span class="paper__label">Poznámka</span>
            <textarea
              class="inline-input inline-input--block"
              rows={2}
              value={note}
              onInput={(e) => setNote((e.target as HTMLTextAreaElement).value)}
              placeholder="—"
              autoFocus
            />
          </section>
        ) : (
          <button
            type="button"
            class="paper__note-toggle"
            onClick={() => setShowNote(true)}
          >
            + Vložiť poznámku
          </button>
        )}

        {supplier && (
          <footer class="paper__foot">
            <p>
              Vystavil: {supplier.name}
              {supplier.phone && <>{"  ·  "}{supplier.phone}</>}
              {supplier.website && <>{"  ·  "}{supplier.website}</>}
              {supplier.email && <>{"  ·  "}{supplier.email}</>}
            </p>
          </footer>
        )}
      </article>
      <div class="editor-save-bar" role="group" aria-label="Uložiť faktúru">
        {saveButtons}
      </div>
    </main>
  );
}

function formatIban(iban: string): string {
  const compact = iban.replace(/\s+/g, "");
  return compact.replace(/(.{4})/g, "$1 ").trim();
}
